import { useState, useEffect } from "react";
import { User, Mail, Phone } from "lucide-react";

interface CustomerInfoFormProps {
  onClearErrors?: () => void;
  onValidationChange?: (isValid: boolean) => void;
  onSmsOptInChange?: (optedIn: boolean) => void;
}

export function CustomerInfoForm({
  onClearErrors,
  onValidationChange,
  onSmsOptInChange,
}: CustomerInfoFormProps) {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
  });
  const [errors, setErrors] = useState({
    name: "",
    email: "",
    phone: "",
  });
  const [smsOptIn, setSmsOptIn] = useState(false);

  const validateField = (name: string, value: string) => {
    switch (name) {
      case "name":
        if (!value.trim()) return "Full name is required";
        if (value.trim().length < 2)
          return "Name must be at least 2 characters";
        return "";
      case "email":
        if (!value.trim()) return "Email address is required";
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value))
          return "Please enter a valid email address";
        return "";
      case "phone":
        if (!value.trim()) return "Phone number is required";
        const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/;
        if (!phoneRegex.test(value.replace(/[\s\-\(\)]/g, "")))
          return "Please enter a valid phone number";
        return "";
      default:
        return "";
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    setFormData((prev) => ({ ...prev, [name]: value }));

    const error = validateField(name, value);
    setErrors((prev) => ({ ...prev, [name]: error }));

    if (onClearErrors) {
      onClearErrors();
    }
  };

  const isFormValid = () => {
    return (
      formData.name.trim() !== "" &&
      formData.email.trim() !== "" &&
      formData.phone.trim() !== "" &&
      Object.values(errors).every((error) => error === "")
    );
  };

  useEffect(() => {
    if (onValidationChange) {
      onValidationChange(isFormValid());
    }
  }, [formData, errors, onValidationChange]);

  const handleSmsOptInChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    setSmsOptIn(checked);
    if (onSmsOptInChange) {
      onSmsOptInChange(checked);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <label
          htmlFor="customer_name"
          className="block text-sm font-semibold text-gray-700 mb-2"
        >
          Full Name *
        </label>
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <User className="h-5 w-5 text-gray-400 mx-2" />
          </div>
          <input
            type="text"
            id="customer_name"
            name="name"
            required
            value={formData.name}
            onChange={handleInputChange}
            className={`w-full ml-1 pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors placeholder-gray-400 ${
              errors.name
                ? "border-red-300 focus:ring-red-500 focus:border-red-500"
                : "border-gray-300"
            }`}
            placeholder="Enter your full name"
          />
        </div>
        {errors.name && (
          <p className="mt-1 text-sm text-red-600 flex items-center">
            <span className="w-1 h-1 bg-red-500 rounded-full mr-2"></span>
            {errors.name}
          </p>
        )}
      </div>

      <div>
        <label
          htmlFor="customer_email"
          className="block text-sm font-semibold text-gray-700 mb-2"
        >
          Email Address *
        </label>
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Mail className="h-5 w-5 text-gray-400 mx-2" />
          </div>
          <input
            type="email"
            id="customer_email"
            name="email"
            required
            value={formData.email}
            onChange={handleInputChange}
            className={`w-full ml-1 pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors placeholder-gray-400 ${
              errors.email
                ? "border-red-300 focus:ring-red-500 focus:border-red-500"
                : "border-gray-300"
            }`}
            placeholder="Enter your email address"
          />
        </div>
        {errors.email && (
          <p className="mt-1 text-sm text-red-600 flex items-center">
            <span className="w-1 h-1 bg-red-500 rounded-full mr-2"></span>
            {errors.email}
          </p>
        )}
      </div>

      <div>
        <label
          htmlFor="customer_phone"
          className="block text-sm font-semibold text-gray-700 mb-2"
        >
          Phone Number *
        </label>
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Phone className="h-5 w-5 text-gray-400 mx-2" />
          </div>
          <input
            type="tel"
            id="customer_phone"
            name="phone"
            required
            value={formData.phone}
            onChange={handleInputChange}
            className={`w-full ml-1 pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors placeholder-gray-400 ${
              errors.phone
                ? "border-red-300 focus:ring-red-500 focus:border-red-500"
                : "border-gray-300"
            }`}
            placeholder="Enter your phone number"
          />
        </div>
        {errors.phone && (
          <p className="mt-1 text-sm text-red-600 flex items-center">
            <span className="w-1 h-1 bg-red-500 rounded-full mr-2"></span>
            {errors.phone}
          </p>
        )}
      </div>

      {/* SMS Opt-in */}
      <div className="rounded-lg border border-gray-200 p-4">
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            id="sms_opt_in"
            name="sms_opt_in"
            checked={smsOptIn}
            onChange={handleSmsOptInChange}
            className="mt-0.5 h-4 w-4 shrink-0 rounded border-gray-300 accent-blue-600 cursor-pointer"
          />
          <span className="text-sm text-gray-600 leading-snug">
            I agree to receive SMS text messages about my reservation from this
            business. Message &amp; data rates may apply. Reply{" "}
            <strong>STOP</strong> to opt out at any time.
          </span>
        </label>
      </div>

      <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
        <div className="flex items-start">
          <div className="w-5 h-5 bg-blue-100 rounded-full flex items-center justify-center mr-3 mt-0.5">
            <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
          </div>
          <div>
            <p className="text-sm text-blue-800 font-medium">
              Secure Information
            </p>
            <p className="text-xs text-blue-600 mt-1">
              Your personal information is encrypted and secure. We&apos;ll only
              use it to process your reservation.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
